import "./MyChatTopics.scss";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import PageTitle from "../../../components/PageTitle/PageTitle";
import { AuthContext } from "../../../contexts/AuthContext";
import AlertModal from "../../../components/AlertModal/AlertModal";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import {
  DynamicCheckbox,
  DynamicInput,
  DynamicSelect,
  DynamicTextarea,
} from "../../../components/Inputs/Inputs";
import Button from "../../../components/Button/Button";
import { Modal } from "../../../components/Modal/Modal";
import { ReactComponent as Close } from "../../../assets/x.svg";
import { CiEdit } from "react-icons/ci";
import Table from "../../../components/Table/Table";
import DisplayInfo from "../../../components/DisplayInfo/DisplayInfo";

// util: slug para topic key
const computeKey = (s = "") =>
  s.normalize("NFKC").toLowerCase().replace(/\s+/g, "");

// limites alinhados com as colunas title_pt/en/es e message_pt/en/es no backend
const TITLE_MAX_LENGTH = 150;
const MESSAGE_MAX_LENGTH = 500;

const emptyForm = {
  id: null,
  key: "",
  title_pt: "",
  title_en: "",
  title_es: "",
  message_pt: "",
  message_en: "",
  message_es: "",
  is_active: true,
};

export default function MyChatTopics() {
  const {
    axiosAuth,
    accessibleHotels = [],
    accessibleGroups = [],
    currentUser,
  } = useContext(AuthContext);

  const isHotelAdmin = currentUser?.role === "hotel_admin";

  // hotel selecionado (de accessibleHotels)
  const hotelOptions = useMemo(
    () =>
      (accessibleHotels || []).map((h) => ({
        value: String(h.id),
        label: h.name,
        group_id: h.group_id, // útil no POST
      })),
    [accessibleHotels]
  );

  const [selectedHotelId, setSelectedHotelId] = useState("");
  useEffect(() => {
    if (!selectedHotelId && hotelOptions.length > 0) {
      setSelectedHotelId(hotelOptions[0].value);
    } else if (
      selectedHotelId &&
      !hotelOptions.some((h) => h.value === selectedHotelId)
    ) {
      setSelectedHotelId(hotelOptions[0]?.value || "");
    }
  }, [hotelOptions, selectedHotelId]);

  const selectedHotel = useMemo(
    () => hotelOptions.find((h) => h.value === selectedHotelId),
    [hotelOptions, selectedHotelId]
  );

  // dados
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);

  // modals
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: "",
    message: "",
  });
  const [confirm, setConfirm] = useState({ open: false, id: null });

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyForm);

  const [saving, setSaving] = useState(false);

  const openError = useCallback((title, message) => {
    setErrorModal({ open: true, title, message });
  }, []);
  const closeError = useCallback(
    () => setErrorModal({ open: false, title: "", message: "" }),
    []
  );

  // retorna topicos por hotel
  const fetchTopics = useCallback(async () => {
    if (!selectedHotelId) {
      setTopics([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axiosAuth.get(`/topics/hotel/${selectedHotelId}`);
      // ordem estável por id asc
      const list = Array.isArray(data)
        ? [...data].sort((a, b) => a.id - b.id)
        : [];
      setTopics(list);
    } catch (err) {
      if (err?.response?.status === 404) {
        setTopics([]);
      } else {
        openError(
          "Erro ao carregar tópicos",
          err?.response?.data?.detail || err.message
        );
      }
    } finally {
      setLoading(false);
    }
  }, [axiosAuth, selectedHotelId, openError]);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  // helpers UI
  const truncate = (s = "", n = 50) => (s.length > n ? `${s.slice(0, n)}…` : s);

  // tabela
  const columns = useMemo(
    () => [
      { key: "title_pt", label: "Tópico" },
      { key: "message_pt_short", label: "Mensagem" },
      {
        key: "status",
        label: "Estado",
        render: (row) => (
          <button
            type="button"
            className={`status-badge ${row._raw.is_active ? "active" : "inactive"}`}
            onClick={(e) => {
              e.stopPropagation();
              handleToggleActive(row._raw);
            }}
            title={row._raw.is_active ? "Clicar para desativar" : "Clicar para ativar"}
          >
            {row._raw.is_active ? "Ativo" : "Inativo"}
          </button>
        ),
      },
      {
        key: "actions",
        label: "Ações",
        render: (row) => (
          <>
            <button
              className="action-button edit"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEdit(row._raw); // passa objeto original
              }}
              title="Editar"
            >
              <CiEdit />
            </button>
            <button
              className="action-button delete"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(row._raw.id);
              }}
              title="Apagar"
            >
              <Close />
            </button>
          </>
        ),
      },
    ],
    []
  );

  const tableData = useMemo(
    () =>
      topics.map((t) => ({
        id: t.id,
        title_pt: t.title_pt,
        message_pt_short: truncate(t.message_pt || "", 50),
        _raw: t, // para facilitar no render das ações
      })),
    [topics]
  );

  // ativar/desativar rapidamente (sem abrir o modal de edição)
  const handleToggleActive = useCallback(
    async (topic) => {
      try {
        await axiosAuth.put(`/topics/${topic.id}`, {
          is_active: !topic.is_active,
        });
        await fetchTopics();
      } catch (err) {
        openError(
          "Erro ao atualizar estado do tópico",
          err?.response?.data?.detail || err.message
        );
      }
    },
    [axiosAuth, fetchTopics, openError]
  );

  // criar
  const openCreateModal = useCallback(() => {
    setCreateForm({ ...emptyForm });
    setIsCreateOpen(true);
  }, []);

  const updateCreate = useCallback((name, value) => {
    setCreateForm((p) => {
      const next = { ...p, [name]: value };
      if (name === "title_en") {
        next.key = computeKey(value);
      }
      return next;
    });
  }, []);

  const handleSubmitCreate = useCallback(async () => {
    if (!selectedHotel) {
      openError("Validação", "Selecione um hotel.");
      return;
    }

    // validação mínima
    const required = [
      "title_pt",
      "title_en",
      "title_es",
      "message_pt",
      "message_en",
      "message_es",
    ];
    for (const f of required) {
      if (!createForm[f]?.trim()) {
        openError("Validação", "Preencha todos os campos obrigatórios.");
        return;
      }
    }

    setSaving(true);

    try {
      // group_id (preferir do hotel; fallback 1º grupo acessível)
      const groupId = selectedHotel.group_id || accessibleGroups[0]?.id || null;
      if (!groupId) {
        openError(
          "Validação",
          "Não foi possível determinar o grupo deste hotel."
        );
        setSaving(false);
        return;
      }
      const key = computeKey(createForm.title_en);
      const payload = {
        group_id: Number(groupId),
        hotel_id: Number(selectedHotel.value),
        key,
        title_pt: createForm.title_pt,
        title_en: createForm.title_en,
        title_es: createForm.title_es,
        message_pt: createForm.message_pt,
        message_en: createForm.message_en,
        message_es: createForm.message_es,
        is_active: createForm.is_active,
      };

      await axiosAuth.post("/topics/", payload);
      setIsCreateOpen(false);
      await fetchTopics();
    } catch (err) {
      openError(
        "Erro ao criar tópico",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setSaving(false);
    }
  }, [
    createForm,
    selectedHotel,
    accessibleGroups,
    axiosAuth,
    fetchTopics,
    openError,
  ]);

  // editar
  const handleOpenEdit = useCallback((t) => {
    setEditForm({
      id: t.id,
      key: computeKey(t.title_en || ""),
      title_pt: t.title_pt || "",
      title_en: t.title_en || "",
      title_es: t.title_es || "",
      message_pt: t.message_pt || "",
      message_en: t.message_en || "",
      message_es: t.message_es || "",
      is_active: t.is_active ?? true,
    });
    setIsEditOpen(true);
  }, []);

  const updateEdit = useCallback((name, value) => {
    setEditForm((p) => {
      const next = { ...p, [name]: value };
      if (name === "title_en") {
        next.key = computeKey(value);
      }
      return next;
    });
  }, []);

  const handleSubmitEdit = useCallback(async () => {
    if (!editForm.id) return;

    // validações simples
    const required = [
      "title_pt",
      "title_en",
      "title_es",
      "message_pt",
      "message_en",
      "message_es",
    ];
    for (const f of required) {
      if (!editForm[f]?.trim()) {
        openError("Validação", "Preencha todos os campos obrigatórios.");
        return;
      }
    }

    setSaving(true);
    try {
      const key = computeKey(editForm.title_en);
      const payload = {
        key,
        title_pt: editForm.title_pt,
        title_en: editForm.title_en,
        title_es: editForm.title_es,
        message_pt: editForm.message_pt,
        message_en: editForm.message_en,
        message_es: editForm.message_es,
        is_active: editForm.is_active,
      };
      await axiosAuth.put(`/topics/${editForm.id}`, payload);
      setIsEditOpen(false);
      await fetchTopics();
    } catch (err) {
      openError(
        "Erro ao guardar tópico",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setSaving(false);
    }
  }, [editForm, axiosAuth, fetchTopics, openError]);

  // apagar
  const handleDeleteClick = useCallback((id) => {
    setConfirm({ open: true, id });
  }, []);

  const confirmDelete = useCallback(async () => {
    const id = confirm.id;
    if (!id) return;
    try {
      await axiosAuth.delete(`/topics/${id}`);
      setConfirm({ open: false, id: null });
      await fetchTopics();
    } catch (err) {
      openError(
        "Erro ao apagar tópico",
        err?.response?.data?.detail || err.message
      );
    }
  }, [confirm, axiosAuth, fetchTopics, openError]);

  return (
    <div className="my-chat-topics">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title || "Ocorreu um erro"}
          message={errorModal.message}
          onClose={closeError}
        />
      )}
      {confirm.open && (
        <ConfirmationModal
          title="Apagar tópico"
          variant="danger"
          message="Deseja apagar este tópico?"
          secText="Cancelar"
          varText="Confirmar"
          onCancel={() => setConfirm({ open: false, id: null })}
          onConfirm={confirmDelete}
        />
      )}

      <PageTitle link="" title="Tópicos" />

      <div className="filters">
        <DynamicSelect
          label="Hotel"
          placeholder="Escolher um hotel"
          value={selectedHotelId}
          onChange={(e) => setSelectedHotelId(e.target.value)}
          options={hotelOptions}
          disabled={loading || hotelOptions.length === 0}
        />
        <div className="right">
          {selectedHotelId && (
            <Button type="button" onClick={openCreateModal} disabled={loading}>
              Novo tópico
            </Button>
          )}
        </div>
      </div>
      <div className="container">
        {!loading && selectedHotelId && topics.length === 0 && (
          <DisplayInfo variant="info" message="Sem tópicos para este hotel." />
        )}
        <Table columns={columns} data={tableData} />
      </div>

      {/* modal de criação */}
      <Modal
        isVisible={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Novo tópico"
        saveText="Adicionar"
        onSave={handleSubmitCreate}
        disableActions={saving}
      >
        <div className="row">
          <DynamicCheckbox
            label="Tópico ativo"
            name="is_active"
            checked={createForm.is_active}
            onChange={(_, v) => updateCreate("is_active", v)}
          />
        </div>
        <div className="row three-col">
          <DynamicInput
            label="Título PT"
            name="title_pt"
            required
            maxLength={TITLE_MAX_LENGTH}
            value={createForm.title_pt}
            onChange={(_, v) => updateCreate("title_pt", v)}
          />
          <DynamicInput
            label="Título EN"
            name="title_en"
            required
            maxLength={TITLE_MAX_LENGTH}
            value={createForm.title_en}
            onChange={(_, v) => updateCreate("title_en", v)}
          />
          <DynamicInput
            label="Título ES"
            name="title_es"
            required
            maxLength={TITLE_MAX_LENGTH}
            value={createForm.title_es}
            onChange={(_, v) => updateCreate("title_es", v)}
          />
        </div>
        <div className="row">
          <DynamicTextarea
            label="Mensagem PT"
            name="message_pt"
            rows={4}
            required
            maxLength={MESSAGE_MAX_LENGTH}
            value={createForm.message_pt}
            onChange={(_, v) => updateCreate("message_pt", v)}
          />
        </div>
        <div className="row">
          <DynamicTextarea
            label="Mensagem EN"
            name="message_en"
            rows={4}
            required
            maxLength={MESSAGE_MAX_LENGTH}
            value={createForm.message_en}
            onChange={(_, v) => updateCreate("message_en", v)}
          />
        </div>
        <div className="row">
          <DynamicTextarea
            label="Mensagem ES"
            name="message_es"
            rows={4}
            required
            maxLength={MESSAGE_MAX_LENGTH}
            value={createForm.message_es}
            onChange={(_, v) => updateCreate("message_es", v)}
          />
        </div>
      </Modal>

      {/* modal de edição */}
      <Modal
        isVisible={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Editar tópico"
        saveText="Guardar"
        onSave={handleSubmitEdit}
        disableActions={saving}
      >
        <div className="row">
          <DynamicCheckbox
            label="Tópico ativo"
            name="is_active"
            checked={editForm.is_active}
            onChange={(_, v) => updateEdit("is_active", v)}
          />
        </div>
        <div className="row three-col">
          <DynamicInput
            label="Título PT"
            name="title_pt"
            required
            maxLength={TITLE_MAX_LENGTH}
            value={editForm.title_pt}
            onChange={(_, v) => updateEdit("title_pt", v)}
          />
          <DynamicInput
            label="Título EN"
            name="title_en"
            required
            maxLength={TITLE_MAX_LENGTH}
            value={editForm.title_en}
            onChange={(_, v) => updateEdit("title_en", v)}
          />
          <DynamicInput
            label="Título ES"
            name="title_es"
            required
            maxLength={TITLE_MAX_LENGTH}
            value={editForm.title_es}
            onChange={(_, v) => updateEdit("title_es", v)}
          />
        </div>
        <div className="row">
          <DynamicTextarea
            label="Mensagem PT"
            name="message_pt"
            rows={4}
            required
            maxLength={MESSAGE_MAX_LENGTH}
            value={editForm.message_pt}
            onChange={(_, v) => updateEdit("message_pt", v)}
          />
        </div>
        <div className="row">
          <DynamicTextarea
            label="Mensagem EN"
            name="message_en"
            rows={4}
            required
            maxLength={MESSAGE_MAX_LENGTH}
            value={editForm.message_en}
            onChange={(_, v) => updateEdit("message_en", v)}
          />
        </div>
        <div className="row">
          <DynamicTextarea
            label="Mensagem ES"
            name="message_es"
            rows={4}
            required
            maxLength={MESSAGE_MAX_LENGTH}
            value={editForm.message_es}
            onChange={(_, v) => updateEdit("message_es", v)}
          />
        </div>
      </Modal>
    </div>
  );
}
